// client/src/pages/wholesaler/WholesalerPricing.js
// Fully translated pricing page with i18next support

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import ConfirmModal from '../../components/common/ConfirmModal';
import { wholesalerAPI } from '../../services/api';
import toast from 'react-hot-toast';

const WholesalerPricing = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['wholesaler']);
  const [currentTier, setCurrentTier] = useState('starter');
  const [loading, setLoading] = useState(true);
  const [estimatedOrders, setEstimatedOrders] = useState(100);
  const [confirmModal, setConfirmModal] = useState(null);

  useEffect(() => {
    fetchCurrentTier();
  }, []);

  const fetchCurrentTier = async () => {
    try {
      const response = await wholesalerAPI.getProfile();
      setCurrentTier(response.data.wholesaler.subscriptionTier || 'starter');
    } catch (err) {
      console.error('Failed to fetch profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const tiers = [
    {
      id: 'starter',
      name: t('pricing.tiers.starter.name', 'Starter'),
      price: 49,
      yearlyPrice: 490,
      productLimit: 1500,
      perOrderFee: 0.40,
      features: [
        t('pricing.features.productSlots', { count: 1500 }, '1,500 product slots'),
        t('pricing.features.perOrderFee', { fee: '0.40' }, '€0.40 per order received'),
        t('pricing.features.catalogDiscountsLimit', { count: 5 }, '5 catalog-wide discounts'),
        t('pricing.features.customerDiscountsLimit', { count: 5 }, '5 customer-specific discounts'),
        t('pricing.features.basicAnalytics', 'Basic analytics dashboard'),
        t('pricing.features.productImageUploads', 'Product image uploads'),
        t('pricing.features.orderManagementSystem', 'Order management system'),
        t('pricing.features.invoiceUpload', 'Invoice upload capability'),
        t('pricing.features.erpExport', 'Export to ERP (CSV/JSON)'),
        t('pricing.features.emailSupport', { hours: '48' }, 'Email support (48hr response)')
      ],
      color: 'blue',
      target: t('pricing.tiers.starter.target', 'Small wholesalers'),
      description: t('pricing.tiers.starter.description', 'Perfect for getting started')
    },
    {
      id: 'professional',
      name: t('pricing.tiers.professional.name', 'Professional'),
      price: 129,
      yearlyPrice: 1290,
      productLimit: 5000,
      perOrderFee: 0.25,
      features: [
        t('pricing.features.productSlots', { count: 5000 }, '5,000 product slots'),
        t('pricing.features.perOrderFeeSavings', { fee: '0.25', savings: 37 }, '€0.25 per order received (37% savings!)'),
        t('pricing.features.catalogDiscountsLimit', { count: 50 }, '50 catalog-wide discounts'),
        t('pricing.features.unlimitedCustomerDiscounts', 'Unlimited customer discounts'),
        t('pricing.features.flashOffersLimit', { count: 5 }, '5 flash offers per month'),
        t('pricing.features.privateCatalog', 'Private catalog mode'),
        t('pricing.features.bulkProductUpload', 'Bulk product upload (CSV)'),
        t('pricing.features.advancedAnalytics', 'Advanced analytics & insights'),
        t('pricing.features.multipleImagesPerProduct', { count: 5 }, 'Up to 5 images per product'),
        t('pricing.features.teamMembers', { count: 3 }, 'Up to 3 team members'),
        t('pricing.features.salesReps', { count: 1 }, '1 sales rep + on-behalf ordering'),
        t('pricing.features.erpExport', 'Export to ERP (CSV/JSON)'),
        t('pricing.features.prioritySupport', { hours: '24' }, 'Priority support (24hr response)')
      ],
      color: 'purple',
      target: t('pricing.tiers.professional.target', 'Growing wholesalers'),
      description: t('pricing.tiers.professional.description', 'Most popular choice'),
      popular: true
    },
    {
      id: 'enterprise',
      name: t('pricing.tiers.enterprise.name', 'Enterprise'),
      price: 299,
      yearlyPrice: 2990,
      productLimit: 'Unlimited',
      perOrderFee: 0.15,
      features: [
        t('pricing.features.unlimitedProductSlots', 'Unlimited product slots'),
        t('pricing.features.perOrderFeeSavings', { fee: '0.15', savings: 62 }, '€0.15 per order received (62% savings!)'),
        t('pricing.features.unlimitedDiscounts', 'Unlimited discounts & flash offers'),
        t('pricing.features.privateCatalog', 'Private catalog mode'),
        t('pricing.features.premiumAnalytics', 'Premium analytics suite'),
        t('pricing.features.unlimitedProductImages', 'Unlimited product images'),
        t('pricing.features.teamMembers', { count: 10 }, 'Up to 10 team members'),
        t('pricing.features.salesReps', { count: 5 }, '5 sales reps + on-behalf ordering'),
        t('pricing.features.bulkProductUpload', 'Bulk product upload (CSV)'),
        t('pricing.features.erpExport', 'Export to ERP (CSV/JSON)'),
        t('pricing.features.dedicatedAccountManager', 'Dedicated account manager'),
        t('pricing.features.priorityPhoneSupport', { hours: '2' }, 'Priority phone support (2hr response)'),
        t('pricing.features.customReporting', 'Custom reporting')
      ],
      color: 'amber',
      target: t('pricing.tiers.enterprise.target', 'Large wholesalers'),
      description: t('pricing.tiers.enterprise.description', 'Maximum features & capacity')
    }
  ];

  const getTierColorClasses = (color, isCurrentTier) => {
    if (isCurrentTier) {
      return {
        border: 'border-green-500',
        headerBg: 'bg-green-50',
        accentText: 'text-green-600',
        button: 'bg-gray-400 cursor-not-allowed'
      };
    }

    const colors = {
      blue: {
        border: 'border-blue-200',
        headerBg: 'bg-blue-50',
        accentText: 'text-blue-600',
        button: 'bg-blue-600 hover:bg-blue-700'
      },
      purple: {
        border: 'border-purple-200',
        headerBg: 'bg-purple-50',
        accentText: 'text-purple-600',
        button: 'bg-purple-600 hover:bg-purple-700'
      },
      amber: {
        border: 'border-amber-200',
        headerBg: 'bg-amber-50',
        accentText: 'text-amber-600',
        button: 'bg-amber-600 hover:bg-amber-700'
      }
    };
    return colors[color];
  };

  const calculateMonthlyCost = (tier) => {
    const subscription = tier.price;
    const orderFees = estimatedOrders * tier.perOrderFee;
    return {
      subscription,
      orderFees,
      total: subscription + orderFees
    };
  };

  const getTierIndex = (tierId) => {
    const order = { starter: 0, professional: 1, enterprise: 2 };
    return order[tierId] || 0;
  };

  const isUpgrade = (targetTier) => {
    return getTierIndex(targetTier) > getTierIndex(currentTier);
  };

  const isDowngrade = (targetTier) => {
    return getTierIndex(targetTier) < getTierIndex(currentTier);
  };

  const handleContactForPlanChange = (tierName, tierPrice, targetTierId) => {
    const isUpgrading = isUpgrade(targetTierId);
    const currentTierName = currentTier.charAt(0).toUpperCase() + currentTier.slice(1);
    
    const message = isUpgrading 
      ? t('pricing.contact.upgradeMessage', {
          tierName,
          price: tierPrice,
          currentTier: currentTierName
        })
      : t('pricing.contact.downgradeMessage', {
          tierName,
          price: tierPrice,
          currentTier: currentTierName
        });
    
    const prompt = isUpgrading
      ? t('pricing.contact.upgradePrompt', { tierName })
      : t('pricing.contact.downgradePrompt', { tierName });

    setConfirmModal({
      title: isUpgrading
        ? t('pricing.contact.upgradeTitle', 'Upgrade Plan')
        : t('pricing.contact.downgradeTitle', 'Change Plan'),
      message: prompt,
      icon: 'info',
      confirmLabel: t('pricing.contact.copyMessage', 'Copy Message'),
      confirmColor: 'blue',
      onConfirm: () => {
        setConfirmModal(null);
        navigator.clipboard.writeText(message).then(() => {
          toast.success(t('pricing.contact.messageCopied'));
        }).catch(() => {
          toast.error(t('pricing.contact.messageDisplay'));
        });
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex justify-center items-center h-64">
          <div className="text-xl text-gray-600">{t('pricing.loading')}</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="container mx-auto px-4 py-8 pt-16 lg:pt-8">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            {t('pricing.title')}
          </h1>
          <p className="text-lg text-gray-600 mb-1">
            {t('pricing.subtitle')}
          </p>
          <p className="text-sm text-gray-500">
            {t('pricing.allPlansInclude')}
          </p>
          {currentTier && (
            <div className="mt-3">
              <span className="inline-block bg-green-500 text-white px-4 py-1 rounded-full text-sm font-bold shadow-lg">
                ✓ {t('pricing.currentPlan')}: {currentTier.charAt(0).toUpperCase() + currentTier.slice(1)}
              </span>
            </div>
          )}
        </div>

        {/* Cost Calculator */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6 max-w-2xl mx-auto">
          <h3 className="text-base font-semibold text-gray-800 mb-3 text-center">
            💡 {t('pricing.estimateMonthlyCost')}
          </h3>
          <div className="flex items-center justify-center gap-4 mb-2">
            <label className="text-sm text-gray-700 font-medium">
              {t('pricing.expectedOrdersPerMonth')}
            </label>
            <input
              type="number"
              min="0"
              value={estimatedOrders}
              onChange={(e) => setEstimatedOrders(parseInt(e.target.value) || 0)}
              className="w-28 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
          </div>
          <p className="text-xs text-gray-500 text-center">
            {t('pricing.adjustToSeeEstimatedCosts')}
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8 mt-6">
          {tiers.map((tier) => {
            const isCurrentTier = tier.id === currentTier;
            const colors = getTierColorClasses(tier.color, isCurrentTier);
            const costs = calculateMonthlyCost(tier);

            return (
              <div
                key={tier.id}
                className={`relative bg-white rounded-lg border-2 ${colors.border} ${
                  tier.popular ? 'shadow-2xl scale-105' : 'shadow-lg'
                }`}
              >
                {/* Popular Badge */}
                {tier.popular && !isCurrentTier && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                    <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-1 rounded-full text-sm font-bold shadow-lg">
                      ⭐ {t('pricing.mostPopular')}
                    </span>
                  </div>
                )}

                {/* Current Plan Badge */}
                {isCurrentTier && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                    <span className="bg-green-500 text-white px-4 py-1 rounded-full text-sm font-bold shadow-lg">
                      ✓ {t('pricing.currentPlan')}
                    </span>
                  </div>
                )}

                {/* Header */}
                <div className={`${colors.headerBg} px-6 py-6 rounded-t-lg ${tier.popular || isCurrentTier ? 'pt-8' : 'pt-6'}`}>
                  <div className="text-center">
                    <h3 className="text-2xl font-bold text-gray-800 mb-1">
                      {tier.name}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">{tier.description}</p>
                    <div className="mb-2">
                      <span className="text-5xl font-bold text-gray-900">€{tier.price}</span>
                      <span className="text-gray-600">{t('pricing.perMonth')}</span>
                    </div>
                    <p className="text-sm text-gray-500">
                      {t('pricing.orYearly', {
                        yearly: tier.yearlyPrice,
                        savings: (tier.price * 12) - tier.yearlyPrice
                      })}
                    </p>
                  </div>
                </div>

                {/* Body */}
                <div className="px-6 py-6">
                  {/* Product Limit */}
                  <div className="text-center mb-4 pb-4 border-b border-gray-200">
                    <div className="text-2xl font-bold text-gray-900 mb-1">
                      {tier.productLimit === 'Unlimited' ? '∞' : tier.productLimit}
                    </div>
                    <div className="text-sm text-gray-600">
                      {tier.productLimit === 'Unlimited' 
                        ? t('pricing.unlimitedProducts')
                        : t('pricing.products')
                      }
                    </div>
                    <div className={`mt-1 text-xs font-semibold ${colors.accentText}`}>
                      €{tier.perOrderFee.toFixed(2)} {t('pricing.perOrder')}
                    </div>
                  </div>

                  {/* Features */}
                  <ul className="space-y-2 mb-4">
                    {tier.features.map((feature, index) => (
                      <li key={index} className="flex items-start text-xs text-gray-700">
                        <span className="text-green-500 mr-2 mt-0.5 flex-shrink-0">✓</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Cost Breakdown */}
                  <div className="bg-gray-50 rounded-lg p-3 mb-4">
                    <h4 className="text-xs font-semibold text-gray-600 uppercase mb-2">
                      {t('pricing.estimatedMonthlyCostShort')}
                    </h4>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('pricing.subscriptionCost')}</span>
                        <span className="font-medium">€{costs.subscription}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">
                          {t('pricing.ordersCost', { count: estimatedOrders, fee: tier.perOrderFee.toFixed(2) })}
                        </span>
                        <span className="font-medium">€{costs.orderFees.toFixed(2)}</span>
                      </div>
                      <div className="border-t border-gray-300 pt-1 mt-1">
                        <div className="flex justify-between font-bold">
                          <span className="text-gray-800">{t('pricing.total')}</span>
                          <span className="text-gray-900 text-base">€{costs.total.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* CTA Button */}
                  {isCurrentTier ? (
                    <button
                      disabled
                      className={`w-full ${colors.button} text-white px-6 py-2.5 rounded-lg font-semibold text-sm`}
                    >
                      {t('pricing.currentPlan')}
                    </button>
                  ) : (
                    <button
                      onClick={() => handleContactForPlanChange(tier.name, tier.price, tier.id)}
                      className={`w-full ${colors.button} text-white px-6 py-2.5 rounded-lg font-semibold transition-colors text-sm`}
                    >
                      {isUpgrade(tier.id) 
                        ? t('pricing.upgradeTo', { tier: tier.name })
                        : t('pricing.changeTo', { tier: tier.name })
                      }
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Seasonal Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-12 max-w-4xl mx-auto">
          <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center gap-2">
            <span>📅</span>
            <span>{t('pricing.seasonal.title')}</span>
          </h3>
          <div className="grid md:grid-cols-2 gap-6 text-sm">
            <div>
              <h4 className="font-semibold text-blue-800 mb-2">
                {t('pricing.seasonal.offSeason')}
              </h4>
              <ul className="space-y-1 text-blue-700">
                <li>• {t('pricing.seasonal.offSeasonBullets.lowerVolumes')}</li>
                <li>• {t('pricing.seasonal.offSeasonBullets.steadyRevenue')}</li>
                <li>• {t('pricing.seasonal.offSeasonBullets.optimizeCatalog')}</li>
                <li>• {t('pricing.seasonal.offSeasonBullets.focusRelationships')}</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-blue-800 mb-2">
                {t('pricing.seasonal.highSeason')}
              </h4>
              <ul className="space-y-1 text-blue-700">
                <li>• {t('pricing.seasonal.highSeasonBullets.volumeIncrease')}</li>
                <li>• {t('pricing.seasonal.highSeasonBullets.tourismDemand')}</li>
                <li>• {t('pricing.seasonal.highSeasonBullets.higherTiersSave')}</li>
                <li>• {t('pricing.seasonal.highSeasonBullets.annualRevenue')}</li>
              </ul>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="max-w-3xl mx-auto mb-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            {t('pricing.faq.title')}
          </h2>
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="font-semibold text-gray-800 mb-2">
                {t('pricing.faq.q1')}
              </h3>
              <p className="text-gray-600 text-sm">
                {t('pricing.faq.a1')}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="font-semibold text-gray-800 mb-2">
                {t('pricing.faq.q2')}
              </h3>
              <p className="text-gray-600 text-sm">
                {t('pricing.faq.a2')}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="font-semibold text-gray-800 mb-2">
                {t('pricing.faq.q3')}
              </h3>
              <p className="text-gray-600 text-sm mb-2">
                {t('pricing.faq.a3')}
              </p>
              <p className="text-gray-600 text-sm">
                <strong>{t('pricing.faq.a3Upgrades').split(':')[0]}:</strong> {t('pricing.faq.a3Upgrades').split(':')[1]}
              </p>
              <p className="text-gray-600 text-sm">
                <strong>{t('pricing.faq.a3Downgrades').split(':')[0]}:</strong> {t('pricing.faq.a3Downgrades').split(':')[1]}
              </p>
            </div>
          </div>
        </div>

        {/* Contact CTA */}
        <div className="text-center bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-8 text-white">
          <h2 className="text-2xl font-bold mb-4">{t('subscription.contactForCustom')}</h2>
          <p className="text-lg mb-6">
            {t('subscription.questions')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="mailto:admin@wholesalehub.gr"
              className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              📧 Email: admin@wholesalehub.gr
            </a>
            <a
              href="tel:+30123456789"
              className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              📱 +30 123 456 7890
            </a>
          </div>
        </div>
      </main>
      <Footer />

      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        message={confirmModal?.message}
        icon={confirmModal?.icon}
        confirmLabel={confirmModal?.confirmLabel}
        confirmColor={confirmModal?.confirmColor}
        onConfirm={confirmModal?.onConfirm}
        onCancel={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default WholesalerPricing;